import { useMemo } from 'react'
import { useCRUD, useFetch, useFetchCb, useFetchUncachedCb } from '@gluedigital/ruse-fetch-extras'
import { useDispatch } from 'react-redux'
import { login, logout } from 'src/store/actions/user'
import { Asset, AssetDetails, LoginRequest, UserLogin, UserLoginWithSecret, Profile, TokenValidate } from 'src/types'
import { filterAssets } from 'src/util/filters'
const base = process.env.API_URL || 'https://ada-api.securitas.es/api'

interface ContactBody {
  description: string
  issueType?: string
  assetId?: string
}

export const useUserActions = () => {
  const doFetch = useFetchUncachedCb()
  const dispatch = useDispatch()
  return {
    login: async ({ associationRequest, code, email, password }: LoginRequest): Promise<UserLogin> => {
      const payload = await doFetch<UserLogin>(base + '/auth/login', {
        body: {
          email,
          password,
          code,
          associationRequest,
          base: 'user'
        }
      })
      dispatch(login(payload))
      return payload
    },
    associate: async (secret: string, id: string): Promise<UserLoginWithSecret> => {
      const payload = await doFetch<UserLoginWithSecret>(`${base}/auth/validate/mfa`, {
        body: {
          secret,
          id
        }
      })
      const { secret: secretResponse, ...user } = payload
      dispatch(login(user))
      return payload
    },
    recoveryStart: (email: string) =>
      doFetch(base + '/auth/recovery', { body: { email } }),
    recoveryComplete: (user: string, token: string, password: string) =>
      doFetch(base + '/auth/recovery/complete', { body: { user, token, password } }),
    contact: (body: ContactBody) =>
      doFetch(base + '/client/contact', { body }),
    logout: () => dispatch(logout()),
    sendToken: (token: string, userId: number) =>
      doFetch(`${base}/auth/send-token`, {
        body: {
          token,
          userId
        }
      })
  }
}

export const useAssets = (): Asset[] =>
  useFetch(base + '/user/actives')

export const useAsset = (slug: string): AssetDetails =>
  useFetch(base + '/user/active/' + slug)

export const useProfile = (): Profile =>
  useFetch(base + '/user/me')

export const useAssetActions = (slug: string, subscriberCode: string) => {
  const doFetch = useFetchUncachedCb()
  const reload = useFetchCb()
  return {
    arm: (location?: [number, number], partitionIds?: string[]) =>
      doFetch(base + '/user/active/' + slug + '/arm', { method: 'PUT', body: { subscriberCode, location, partitionIds } }),
    disarm: (location?: [number, number], partitionIds?: string[]) =>
      doFetch(base + '/user/active/' + slug + '/disarm', { method: 'PUT', body: { subscriberCode, location, partitionIds } }),
    refresh: () =>
      reload(base + '/user/active/' + slug)
  }
}

// Filtered views

export const useFilteredAssets = (filter: string, location?: number[]): Asset[] => {
  const assets = useAssets()
  return useMemo(() => assets.filter(filterAssets(filter, location)), [assets, filter, location])
}

// CRUD
export const useProfileCRUD = () => useCRUD<string, Profile>(base + '/user')

export const useConfirmToken = (user: string, token: string): TokenValidate =>
  useFetch(base + '/auth/recovery/checkIsValidToken', { body: { user, token } })
